import {
  createGrantGroup,
  deleteGrantGroup,
  listGrantGroups,
  updateGrantGroup,
} from "@/api/grantGroups";
import { toast } from "@/shared/hooks/useToast";
import { useCallback, useMemo, useState } from "react";
import { PAGE_ITEMS_FOR_GRANT, PAGE_KEYS_FOR_GRANT } from "../libs/pageGrantCatalog";
import type {
  GrantValue,
  GrantsByPageKey,
  PermissionGroupModalMode,
  PermissionGroupRow,
} from "../types/permissionGroups";

function emptyGrants(): GrantsByPageKey {
  const g: GrantsByPageKey = {};
  PAGE_KEYS_FOR_GRANT.forEach((k) => (g[k] = "권한 없음"));
  return g;
}

function expandAllGrant(grants: GrantsByPageKey, allGrantValue: GrantValue) {
  if (allGrantValue === "권한 없음" || allGrantValue === "reader") {
    for (const item of PAGE_ITEMS_FOR_GRANT) grants[item.key] = allGrantValue;
  } else {
    for (const item of PAGE_ITEMS_FOR_GRANT) grants[item.key] = item.maxGrant;
  }
}

function updateAllPagesGrant(grants: GrantsByPageKey) {
  const allPagesAreMax = PAGE_ITEMS_FOR_GRANT.every((item) => grants[item.key] === item.maxGrant);
  if (allPagesAreMax) {
    grants["all"] = "writer";
    return;
  }

  const allPagesAreReaderOrHigher = PAGE_ITEMS_FOR_GRANT.every(
    (item) => grants[item.key] !== "권한 없음"
  );
  grants["all"] = allPagesAreReaderOrHigher ? "reader" : "권한 없음";
}

function buildFinalGrantToStore(grants: GrantsByPageKey) {
  const temp: Record<string, GrantValue> = {};
  for (const [k, v] of Object.entries(grants)) if (v !== "권한 없음") temp[k] = v;

  const all = temp["all"];
  if (all === "writer") return { all: "writer" as const };
  if (all === "reader") {
    const final: Record<string, GrantValue> = { all: "reader" };
    for (const [k, v] of Object.entries(temp)) if (k !== "all" && v === "writer") final[k] = v;
    return final;
  }
  return temp;
}

export function usePermissionGroups() {
  const [isLoading, setIsLoading] = useState(false);

  const [rows, setRows] = useState<PermissionGroupRow[]>([]);
  const [grantsById, setGrantsById] = useState<Record<number, Record<string, GrantValue>>>({});

  const [selectedIds, setSelectedIds] = useState<number[]>([]);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<PermissionGroupModalMode>("추가");
  const [editingId, setEditingId] = useState<number | null>(null);

  const [groupName, setGroupName] = useState("");
  const [grants, setGrants] = useState<GrantsByPageKey>(() => emptyGrants());
  const [errMsg, setErrMsg] = useState("");

  const resetForm = useCallback(() => {
    setGroupName("");
    setGrants(emptyGrants());
    setErrMsg("");
    setEditingId(null);
  }, []);

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      const { grant_groups } = await listGrantGroups();

      const nextGrantsById: Record<number, Record<string, GrantValue>> = {};
      const nextRows: PermissionGroupRow[] = grant_groups.map((g) => {
        // API는 reader/writer만 오므로 GrantValue로 캐스팅 가능
        nextGrantsById[g.id] = g.grant as Record<string, GrantValue>;
        return {
          id: g.id,
          name: g.name,
          grantPreview: JSON.stringify(g.grant).slice(0, 100),
        };
      });

      setGrantsById(nextGrantsById);
      setRows(nextRows);
      setSelectedIds([]);
      resetForm();
      setModalOpen(false);
      setModalMode("추가");
    } catch (e) {
      toast({
        title: "로드 실패",
        description: e instanceof Error ? e.message : "권한 그룹 목록을 불러오지 못했습니다.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  }, [resetForm]);

  const openAdd = useCallback(() => {
    setModalMode("추가");
    resetForm();
    setModalOpen(true);
  }, [resetForm]);

  const openEditByRow = useCallback(
    (row: PermissionGroupRow) => {
      setModalMode("수정");
      setEditingId(row.id);
      setGroupName(row.name);
      setErrMsg("");

      const dbGrants = grantsById[row.id] ?? {};
      const next = emptyGrants();

      const allGrantValue = (dbGrants["all"] ?? "권한 없음") as GrantValue;
      expandAllGrant(next, allGrantValue);
      Object.assign(next, dbGrants); // db override
      updateAllPagesGrant(next);

      setGrants(next);
      setModalOpen(true);
    },
    [grantsById]
  );

  const closeModal = useCallback(() => {
    setModalOpen(false);
    resetForm();
    setModalMode("추가");
  }, [resetForm]);

  const setGrantForPage = useCallback((pageKey: string, value: GrantValue) => {
    setGrants((prev) => {
      const next = { ...prev, [pageKey]: value };

      if (pageKey === "all") {
        expandAllGrant(next, value);
      } else {
        updateAllPagesGrant(next);
      }
      return next;
    });
  }, []);

  const submit = useCallback(async () => {
    const name = groupName.trim();
    if (!name) {
      setErrMsg("권한 그룹 이름을 입력해주세요.");
      return;
    }

    const finalGrant = buildFinalGrantToStore(grants);

    setIsLoading(true);
    try {
      if (modalMode === "추가") {
        const { grant_group } = await createGrantGroup({
          name,
          grant: finalGrant as Record<string, GrantValue>,
        });

        setRows((prev) =>
          [
            ...prev,
            {
              id: grant_group.id,
              name: grant_group.name,
              grantPreview: JSON.stringify(grant_group.grant).slice(0, 100),
            },
          ].sort((a, b) => a.id - b.id)
        );
        setGrantsById((prev) => ({ ...prev, [grant_group.id]: grant_group.grant }));
      } else {
        if (editingId == null) return;

        const { grant_group } = await updateGrantGroup(editingId, {
          name,
          grant: finalGrant as Record<string, GrantValue>,
        });

        setRows((prev) =>
          prev.map((r) =>
            r.id === grant_group.id
              ? {
                  ...r,
                  name: grant_group.name,
                  grantPreview: JSON.stringify(grant_group.grant).slice(0, 100),
                }
              : r
          )
        );
        setGrantsById((prev) => ({ ...prev, [grant_group.id]: grant_group.grant }));
      }

      toast({
        title: "완료",
        description: `권한 그룹이 ${modalMode === "추가" ? "추가" : "수정"}되었습니다.`,
      });
      closeModal();
    } catch (e) {
      toast({
        title: "요청 실패",
        description: e instanceof Error ? e.message : "처리 중 오류가 발생했습니다.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  }, [groupName, grants, modalMode, editingId, closeModal]);

  const deleteSelected = useCallback(async () => {
    if (selectedIds.length === 0) return;

    setIsLoading(true);
    try {
      await Promise.all(selectedIds.map((id) => deleteGrantGroup(id)));

      setRows((prev) => prev.filter((r) => !selectedIds.includes(r.id)));
      setGrantsById((prev) => {
        const next = { ...prev };
        selectedIds.forEach((id) => delete next[id]);
        return next;
      });
      setSelectedIds([]);

      toast({ title: "삭제 완료", description: "선택된 그룹이 삭제되었습니다." });
    } catch (e) {
      toast({
        title: "삭제 실패",
        description: e instanceof Error ? e.message : "삭제 중 오류가 발생했습니다.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  }, [selectedIds]);

  const summary = useMemo(
    () => ({ total: rows.length, selected: selectedIds.length }),
    [rows.length, selectedIds.length]
  );

  return {
    isLoading,
    rows,
    selectedIds,
    setSelectedIds,

    modalOpen,
    modalMode,
    groupName,
    setGroupName,
    grants,
    errMsg,

    summary,

    load,
    openAdd,
    openEditByRow,
    closeModal,
    setGrantForPage,
    submit,
    deleteSelected,
  };
}
